/**
 * AutomationConditionRowView — pure presentation for one editable condition of
 * an automation rule (SE-5506).
 *
 * Renders the condition-type picker, then the value control appropriate to that
 * type: a comparator + numeric field, a text field, or a stage picker. The
 * `numericValue` field is a plain text input (keyboard `decimal-pad`) so the
 * author's exact decimal string is captured verbatim — never a number cast.
 * @remarks View component — no business logic, hooks, or state.
 */

import { Trash2 } from "lucide-react-native";
import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Input } from "@/components/atoms/Input";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";

import RuleSelectField, { type RuleSelectOption } from "../RuleSelectField";
import type { ConditionValueKind } from "../../../utils/automationRuleOptions";

/** Props for {@link AutomationConditionRowView}. */
export interface AutomationConditionRowViewProps {
  /** Selected condition type value. */
  readonly conditionType: string;
  /** Which value control to show for the selected condition type. */
  readonly valueKind: ConditionValueKind;
  /** Selected comparator value (numeric/text kinds). */
  readonly comparator: string;
  /** Numeric threshold as a decimal string (preserved verbatim). */
  readonly numericValue: string;
  /** Text value (text kind). */
  readonly textValue: string;
  /** Selected stage id (stage kind). */
  readonly stageValue: string;
  /** Condition-type options (full enum). */
  readonly conditionTypeOptions: readonly RuleSelectOption[];
  /** Comparator options (full enum). */
  readonly comparatorOptions: readonly RuleSelectOption[];
  /** Stage options for a stage-occupancy condition. */
  readonly stageOptions: readonly RuleSelectOption[];
  /** Condition-type change handler. */
  readonly onConditionTypeChange: (value: string) => void;
  /** Comparator change handler. */
  readonly onComparatorChange: (value: string) => void;
  /** Numeric-value change handler. */
  readonly onNumericValueChange: (value: string) => void;
  /** Text-value change handler. */
  readonly onTextValueChange: (value: string) => void;
  /** Stage change handler. */
  readonly onStageChange: (value: string) => void;
  /** Remove-this-condition handler. */
  readonly onRemove: () => void;
  /** Index (1-based) for accessibility labels. */
  readonly displayIndex: number;
}

/**
 * Render the value control for the condition's value kind.
 * @param props - The row props.
 * @returns The value control node.
 */
const renderValueControl = (props: AutomationConditionRowViewProps) => {
  if (props.valueKind === "stage") {
    return (
      <RuleSelectField
        label="Stage"
        placeholder="Choose a stage"
        value={props.stageValue}
        options={props.stageOptions}
        onValueChange={props.onStageChange}
        testID="condition-stage"
      />
    );
  }

  if (props.valueKind === "text") {
    return (
      <Stack className="flex-1 gap-2" direction="horizontal">
        <Box className="w-40">
          <RuleSelectField
            label="Comparator"
            placeholder="Choose a comparator"
            value={props.comparator}
            options={props.comparatorOptions}
            onValueChange={props.onComparatorChange}
            testID="condition-comparator"
          />
        </Box>
        <Box className="flex-1">
          <Input
            testID="condition-text-value"
            field={{
              value: props.textValue,
              onChangeText: props.onTextValueChange,
              placeholder: "Value",
            }}
          />
        </Box>
      </Stack>
    );
  }

  return (
    <Stack className="flex-1 gap-2" direction="horizontal">
      <Box className="w-40">
        <RuleSelectField
          label="Comparator"
          placeholder="Choose a comparator"
          value={props.comparator}
          options={props.comparatorOptions}
          onValueChange={props.onComparatorChange}
          testID="condition-comparator"
        />
      </Box>
      <Box className="flex-1">
        <Input
          testID="condition-numeric-value"
          field={{
            value: props.numericValue,
            onChangeText: props.onNumericValueChange,
            // NOT "0": a numeric placeholder renders an empty REQUIRED field as
            // though it already held a threshold, which is what made the disabled
            // Save button unexplainable to the author (SE-6531).
            placeholder: "Value",
            keyboardType: "decimal-pad",
          }}
        />
      </Box>
    </Stack>
  );
};

/**
 * Render one condition editor row.
 * @param props - {@link AutomationConditionRowViewProps}
 * @returns The condition row.
 */
const AutomationConditionRowView = (props: AutomationConditionRowViewProps) => (
  <Box
    testID="automation-condition-row"
    className="gap-2 rounded-md border border-outline-subtle p-3"
  >
    <Stack direction="horizontal" className="items-start justify-between gap-2">
      <Box className="flex-1">
        <RuleSelectField
          label={`Condition ${props.displayIndex}`}
          placeholder="Choose a condition"
          value={props.conditionType}
          options={props.conditionTypeOptions}
          onValueChange={props.onConditionTypeChange}
          testID="condition-type"
        />
      </Box>
      <Pressable
        onPress={props.onRemove}
        accessibilityLabel={`Remove condition ${props.displayIndex}`}
        testID="condition-remove"
        className="pt-6"
      >
        <Icon as={Trash2} size="sm" className="text-status-error" />
      </Pressable>
    </Stack>
    {renderValueControl(props)}
  </Box>
);

AutomationConditionRowView.displayName = "AutomationConditionRowView";

export default memo(AutomationConditionRowView);
